// La messagerie — liste des conversations à gauche, discussion à droite.
//
// Septième écran migré, et le premier qui ait du COMPORTEMENT et pas seulement
// de l'affichage : un fil qui doit rester collé en bas quand un message arrive,
// mais pas si l'on est remonté lire plus haut ; une zone de saisie qui grandit ;
// Entrée qui envoie, Maj+Entrée qui va à la ligne.
//
// Un défaut de l'ancienne version corrigé au passage, et il coûtait de l'argent :
// la bande « À qui écrire » rechargeait l'annuaire ENTIER à chaque changement de
// signal — chaque membre qui se connecte ou se déconnecte relançait une lecture
// de tous les profils. En ligne, ces lectures sont facturées. Ici l'annuaire est
// lu UNE fois à l'ouverture, et les suggestions se recalculent sans le relire.

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Store from "../../store.js";
import { ilYA, heure, toast, apercuImage } from "../../ui.js";
import { jouer } from "../../sons.js";
import { suggererContacts, expliquer } from "../../algorithmes.js";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Presence from "../Presence.jsx";
import Icone from "../Icone.jsx";
import TexteRiche from "../TexteRiche.jsx";
import Vide from "../Vide.jsx";
import { useAbonnement, useSignaux, useMembre } from "../hooks.js";
import { creerConversation } from "../../views/conversation-creer.js";
import { confirmer } from "../../ui.js";
import { Palette, Resume, Pastille, resumer, useAppuiLong } from "../Reactions.jsx";

/* ------------------------------------------------------------- à qui écrire */

/**
 * « À qui écrire » : les amis connectés, ceux avec qui l'échange est vivant, et
 * surtout les amitiés récentes jamais suivies d'un mot. Calcul dans
 * js/algorithmes.js ; un clic ouvre directement la conversation.
 */
function AQuiEcrire({ ctx }) {
  const [candidats, setCandidats] = useState([]);
  const versionSignaux = useSignaux(ctx.signaux);

  // L'annuaire, une seule fois. C'est la lecture coûteuse ; les signaux, eux,
  // changent souvent et n'ont aucun besoin qu'on la refasse.
  useEffect(() => {
    let vivant = true;
    (async () => {
      const liste = (await Store.users.search("")).filter((u) => u.uid !== ctx.moi.uid);
      if (!vivant) return;
      ctx.signaux?.apprendreMembres(liste);
      setCandidats(liste);
    })();
    return () => {
      vivant = false;
    };
  }, [ctx]);

  const { resultats, enLigne } = useMemo(() => {
    if (!ctx.signaux || !candidats.length) return { resultats: [], enLigne: new Set() };
    const s = ctx.signaux.instantane();
    return { resultats: suggererContacts(candidats, s, { combien: 4 }), enLigne: s.enLigne };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidats, versionSignaux]);

  if (!resultats.length) return null;

  const ecrire = async (uid) => {
    location.hash = `#/messages/${await Store.convos.ensure(uid)}`;
  };

  return (
    <div className="convo-suggestions">
      <span className="convo-suggestions__titre">À qui écrire</span>
      <div className="convo-suggestions__liste">
        {resultats.map(({ membre: u, raisons }) => (
          <button
            className="convo-suggestion"
            title={expliquer(raisons, 1)[0] || ""}
            onClick={() => ecrire(u.uid)}
            key={u.uid}
          >
            <span className="avatar-enveloppe">
              <Avatar membre={u} taille={42} />
              {enLigne.has(u.uid) && <span className="point-vert point-vert--sur-avatar" />}
            </span>
            <span className="petit">
              {u.nom.split(" ")[0]}
              <Certifie membre={u} taille={11} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------------------------- « en train d'écrire » : le temps

   On enregistre l'INSTANT de la dernière frappe, jamais un booléen — un booléen
   resterait allumé pour toujours si l'autre fermait l'application au milieu
   d'une phrase. C'est donc celui qui lit qui décide qu'au-delà de six secondes,
   la frappe est finie ; et comme un instant qui vieillit ne déclenche aucun
   événement, il faut regarder l'heure de temps en temps. */

const FENETRE_FRAPPE = 6000;
const ecritEncore = (convo, uid) => Date.now() - (convo?.frappe?.[uid] || 0) < FENETRE_FRAPPE;

/** Fait battre l'horloge pendant qu'une frappe est en cours, et seulement alors. */
function useHorlogeFrappe(actif) {
  const [, battre] = useState(0);
  useEffect(() => {
    if (!actif) return;
    const t = setInterval(() => battre((n) => n + 1), 1500);
    return () => clearInterval(t);
  }, [actif]);
}

/* ------------------------------------------------- nommer une conversation

   Un tête-à-tête porte le nom de l'autre. Un groupe porte le sien, et s'il n'en
   a pas, celui de ses membres — « Grâce, Josué et vous », comme sur Messenger.
   Sans cela, une conversation sans nom s'appellerait « Conversation », et l'on
   ne saurait plus laquelle est laquelle. */

function useNomConversation(convo, ctx) {
  const autres = (convo?.membres || []).filter((m) => m !== ctx.moi.uid);
  const [noms, setNoms] = useState(null);

  useEffect(() => {
    if (!convo?.groupe || convo.nom) return;
    let vivant = true;
    Promise.all(autres.slice(0, 3).map((id) => ctx.profil(id))).then(
      (us) => vivant && setNoms(us.map((u) => (u?.nom || "Membre").split(" ")[0]))
    );
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convo?.id, convo?.nom, convo?.groupe]);

  if (!convo?.groupe) return null;
  if (convo.nom) return convo.nom;
  if (!noms) return "…";
  const reste = autres.length - noms.length;
  return `${noms.join(", ")}${reste > 0 ? ` +${reste}` : ""} et vous`;
}

/* --------------------------------------------------- liste des conversations */

/** Une ligne de la liste : avec qui, le dernier mot, et s'il reste à lire. */
function LigneConvo({ convo, ctx, actif }) {
  const autres = convo.membres.filter((m) => m !== ctx.moi.uid);
  const autreId = autres[0] || ctx.moi.uid;
  const [autre, setAutre] = useState(null);
  const nomGroupe = useNomConversation(convo, ctx);

  useEffect(() => {
    let vivant = true;
    ctx.profil(autreId).then((u) => vivant && setAutre(u || { nom: "Membre supprimé" }));
    return () => {
      vivant = false;
    };
  }, [autreId, ctx]);

  const nonLu = convo.lu && convo.lu[ctx.moi.uid] === false;
  // Dans un groupe, n'importe lequel des autres peut être en train d'écrire.
  const frappe = autres.some((id) => ecritEncore(convo, id));
  useHorlogeFrappe(frappe);

  return (
    <a
      className={`convo${actif ? " est-actif" : ""}${nonLu ? " est-non-lu" : ""}`}
      href={`#/messages/${convo.id}`}
    >
      {convo.groupe ? (
        <span className="convo-groupe" aria-hidden="true">👥</span>
      ) : (
        <Avatar membre={autre} taille={46} />
      )}
      <span className="convo__texte">
        <strong>
          {convo.groupe ? nomGroupe : autre ? autre.nom : "…"}
          {!convo.groupe && <Certifie membre={autre} taille={12} />}
        </strong>
        {/* La frappe passe devant le dernier message : c'est la nouvelle la plus
            fraîche, et c'est celle qui donne envie d'ouvrir. */}
        {frappe ? (
          <span className="convo__extrait convo__extrait--frappe">en train d'écrire…</span>
        ) : (
          <span className="convo__extrait">{convo.dernier || "Nouvelle conversation"}</span>
        )}
      </span>
      <span className="convo__meta">
        {convo.dernierAt ? ilYA(convo.dernierAt) : ""}
        {nonLu && <span className="pastille" />}
      </span>
      {/* ⚠️ SUPPRIMER LA CONVERSATION — 24 octobre 2026, demandé par Josaphat.
          Pour SOI seulement : rien n'est détruit, l'autre ne voit rien, et un
          nouveau message la fait revenir — comme Messenger. */}
      <button
        type="button"
        className="convo__supprimer"
        aria-label="Supprimer la conversation"
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!(await confirmer(
            "Supprimer cette conversation ?",
            "Elle disparaît de votre liste — pour vous seulement. Un nouveau message la fera revenir."
          ))) return;
          try {
            await Store.convos.supprimer(convo.id);
            toast("Conversation supprimée");
            if (actif) location.hash = "#/messages";
          } catch (err) {
            toast(err.message || "Suppression impossible", "erreur");
          }
        }}
      >
        <Icone nom="fermer" taille={14} />
      </button>
    </a>
  );
}

function ListeConvos({ convos, ctx, ouvert }) {
  if (convos === null) return null;
  if (!convos.length)
    return (
      <Vide
        symbole="✉️"
        titre="Aucune conversation"
        texte="Choisissez quelqu'un ci-dessus, ou cherchez dans l'annuaire."
      />
    );

  return convos.map((c) => (
    <LigneConvo convo={c} ctx={ctx} actif={c.id === ouvert} key={c.id} />
  ));
}

/* --------------------------------------------------------- messages vocaux

   Au Bénin on parle souvent plutôt qu'on écrit : un vocal est parfois le seul
   message qu'une personne âgée enverra jamais. C'est donc une fonction de
   premier plan, pas un supplément.

   Deux précautions qui comptent :
   · le micro n'est demandé qu'au MOMENT d'enregistrer, jamais à l'ouverture de
     l'écran — une application qui réclame le micro sans raison inquiète ;
   · le flux est refermé à la fin, sinon la pastille rouge de l'appareil reste
     allumée et fait croire qu'on écoute encore.  */

const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
const DUREE_MAX = 120; // deux minutes : au-delà, on écrit ou on téléphone

function useEnregistreur() {
  const [enCours, setEnCours] = useState(false);
  const [secondes, setSecondes] = useState(0);
  const machine = useRef(null);
  const morceaux = useRef([]);
  const horloge = useRef(null);
  /* La durée vit AUSSI dans une référence : l'état sert à l'affichage, la
     référence à la vérité — un état lu dans une fermeture serait celui d'il y
     a un rendu, et la durée enregistrée aurait une seconde de retard. */
  const chrono = useRef(0);

  const arreterFlux = () => {
    machine.current?.stream?.getTracks().forEach((p) => p.stop());
    clearInterval(horloge.current);
  };

  // Quitter l'écran en pleine phrase ne doit pas laisser le micro ouvert.
  useEffect(() => () => arreterFlux(), []);

  const demarrer = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined")
      return toast("Cet appareil ne sait pas enregistrer de son", "erreur");
    try {
      const flux = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = MIMES.find((m) => MediaRecorder.isTypeSupported?.(m));
      const m = new MediaRecorder(flux, type ? { mimeType: type } : undefined);
      morceaux.current = [];
      m.ondataavailable = (e) => e.data.size && morceaux.current.push(e.data);
      /* ⚠️ `onstop` EST BRANCHÉ ICI, PAS DANS `arreter` — 20 octobre 2026.
         Il ne l'était que dans `arreter` : le plafond des deux minutes
         appelait `m.stop()` SANS personne pour l'entendre. L'enregistreur
         s'arrêtait en silence, le micro restait allumé, la barre figée à
         2:00 — et « Envoyer » comme « Annuler » tombaient sur un enregistreur
         déjà inactif : deux minutes de parole perdues, sans un mot. Trouvé à
         l'audit. Ce branchement-ci libère le micro quoi qu'il arrive ;
         `arreter` le remplace quand c'est lui qui provoque l'arrêt. */
      m.onstop = () => arreterFlux();
      m.start();
      machine.current = m;
      chrono.current = 0;
      setSecondes(0);
      setEnCours(true);
      /* ⚠️ LE PLAFOND VIT DANS LA MINUTERIE, PAS DANS L'UPDATER. Un updater
         d'état doit rester pur : `m.stop()` y était rappelé à chaque tour —
         et deux fois par tour en mode strict. Ici, une fois, puis la
         minuterie meurt avec `arreterFlux`. La barre reste affichée à 2:00 :
         l'enregistrement est COMPLET, il attend qu'on l'envoie ou qu'on
         l'annule. */
      horloge.current = setInterval(() => {
        chrono.current += 1;
        setSecondes(Math.min(chrono.current, DUREE_MAX));
        if (chrono.current >= DUREE_MAX && m.state !== "inactive") m.stop();
      }, 1000);
    } catch {
      toast("Micro refusé. Autorisez-le dans les réglages de l'appareil.", "erreur");
    }
  };

  /* Clôt l'enregistrement et fabrique le fichier — que l'arrêt vienne du
     membre ou du plafond des deux minutes. Rend null si l'on annule. */
  const finir = (garder) => {
    arreterFlux();
    setEnCours(false);
    const m = machine.current;
    machine.current = null;
    if (!garder || !m || !morceaux.current.length) return null;
    const type = m.mimeType || "audio/webm";
    const blob = new Blob(morceaux.current, { type });
    const ext = type.includes("mp4") ? "m4a" : "webm";
    return { fichier: new File([blob], `vocal.${ext}`, { type }), duree: Math.min(chrono.current, DUREE_MAX) };
  };

  /** Rend le fichier enregistré, ou null si l'on a annulé. */
  const arreter = (garder = true) =>
    new Promise((resoudre) => {
      const m = machine.current;
      if (!m) return resoudre(null);
      /* Déjà arrêté par le plafond : les morceaux sont là, on les prend. */
      if (m.state === "inactive") return resoudre(finir(garder));
      m.onstop = () => resoudre(finir(garder));
      m.stop();
    });

  return { enCours, secondes, demarrer, arreter };
}

/** « 0:07 » — une durée se lit en minutes et secondes, jamais en secondes seules. */
/** « 1,4 Mo » plutôt que « 1468006 » : personne ne lit des octets. */
export const poidsLisible = (o) =>
  !o ? "" : o < 1024 * 1024 ? `${Math.max(1, Math.round(o / 1024))} Ko` : `${(o / 1048576).toFixed(1).replace(".", ",")} Mo`;

export const dureeLisible = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

/* ----------------------------------------------------------- la discussion */

const memeJour = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

const jourLisible = (ts) =>
  new Date(ts).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

/**
 * Le fil des messages.
 *
 * Le fil doit rester collé en bas quand un message arrive — mais SEULEMENT si
 * l'on était déjà en bas : sinon on arracherait des yeux du lecteur le message
 * qu'il était en train de relire. L'ancienne version mesurait la position juste
 * avant de réécrire le fil. React écrit quand il veut, donc on retient la
 * position au fil du défilement, ce qui est d'ailleurs plus juste.
 */
/**
 * Un trait : le message est parti. Deux traits : il a été ouvert.
 *
 * La conversation retient jusqu'à QUAND chacun a lu (`luA`), et non simplement
 * s'il a lu : sans cela, ouvrir la conversation une fois marquerait comme lus
 * tous les messages à venir. Les comptes ouverts avant cette version n'ont pas
 * ce repère — on se rabat alors sur l'ancienne information, plus grossière,
 * plutôt que d'afficher un trait unique à tort sur d'anciennes discussions.
 */
/**
 * « Vu par Marie, Paul » — qui a ouvert mon dernier message, dans un groupe.
 * Le repère est `luA` (jusqu'à quand chacun a lu) ; les comptes d'avant ce
 * repère retombent sur `lu`. Personne ne l'a vu : on n'affiche rien — les
 * traits au-dessus disent déjà « envoyé ».
 */
function VuPar({ convo, message, ctx }) {
  const lecteurs = (convo?.membres || []).filter((id) => {
    if (id === ctx.moi.uid) return false;
    const luA = convo?.luA?.[id];
    return luA === undefined ? convo?.lu?.[id] === true : luA >= message.createdAt;
  });
  const [noms, setNoms] = useState([]);
  useEffect(() => {
    let vivant = true;
    Promise.all(lecteurs.map((id) => ctx.profil(id).catch(() => null))).then(
      (fiches) => vivant && setNoms(fiches.filter(Boolean).map((u) => (u.nom || "").split(" ")[0]))
    );
    return () => { vivant = false; };
  }, [lecteurs.join(","), ctx]);
  if (!noms.length) return null;
  return <div className="vu-par">Vu par {noms.join(", ")}</div>;
}

function Accuse({ message, convo, autreId, ctx }) {
  // Dans un groupe, « lu » veut dire lu par TOUT LE MONDE — sinon la marque
  // mentirait à celui qui l'a écrit.
  const destinataires = convo?.groupe
    ? (convo.membres || []).filter((m) => m !== ctx.moi.uid)
    : [autreId];
  const lu = destinataires.length
    ? destinataires.every((id) => {
        const luA = convo?.luA?.[id];
        return luA === undefined ? convo?.lu?.[id] === true : luA >= message.createdAt;
      })
    : false;

  return (
    <span
      className={`bulle__accuse bulle__accuse--${lu ? "lu" : "envoye"}`}
      title={lu ? "Lu" : "Envoyé"}
      aria-label={lu ? "Lu" : "Envoyé, pas encore ouvert"}
    >
      <Icone nom={lu ? "traits" : "trait"} taille={15} />
    </span>
  );
}

/* ------------------------------------------------- ce qu'on peut faire d'une bulle

   Répondre, corriger, retirer, réagir. Rien de tout cela n'existait, et les
   règles du serveur l'interdisaient formellement (`allow update, delete: if
   false`). Elles ne s'ouvrent pas en grand pour autant : chacun de ces gestes a
   sa propre branche dans `firestore.rules`, et deux d'entre elles ne sont
   ouvertes qu'à l'auteur du message. */

/** Le texte court qui représente un message quand on le cite ou qu'on y répond. */
function apercuDe(m) {
  if (m.supprime) return "Message supprimé";
  if (m.texte) return m.texte;
  if (m.image) return "Photo";
  if (m.audio) return "Message vocal";
  if (m.fichier?.url) return m.fichier.nom || "Document";
  return "Message";
}

/** Le bloc cité au-dessus d'une réponse. Le toucher remonte au message d'origine. */
function Citation({ repondA, ctx }) {
  const auteur = useMembre(ctx, repondA.auteurId);
  const remonter = () => {
    const cible = document.querySelector(`[data-mid="${CSS.escape(repondA.id)}"]`);
    if (!cible) return; // trop ancien pour être encore chargé : on ne fait rien
    cible.scrollIntoView({ block: "center", behavior: "smooth" });
    cible.classList.add("bulle--visee");
    setTimeout(() => cible.classList.remove("bulle--visee"), 1400);
  };
  return (
    <button type="button" className="bulle__citation" onClick={remonter}>
      <span className="bulle__citation-qui">{auteur.nom}</span>
      <span className="bulle__citation-texte">{repondA.apercu}</span>
    </button>
  );
}

/**
 * Le statut cité au-dessus d'une réponse — la vignette de Messenger.
 *
 * ⚠️ ELLE NE RENVOIE NULLE PART, ET C'EST VOULU. Un statut vit vingt-quatre
 * heures ; la conversation reste. Un lien mènerait donc, la plupart du temps,
 * vers un statut effacé — une impasse à laquelle on ne comprend rien. On
 * montre ce à quoi la personne a répondu, et on s'arrête là.
 *
 * L'image, elle, disparaît avec le statut : le fichier est supprimé pour de
 * bon à l'expiration. `onError` retire alors la vignette et ne laisse que le
 * libellé — la bulle reste lisible au lieu d'afficher une image cassée.
 */
function CitationStatut({ statut, moi }) {
  const [imageMorte, setImageMorte] = useState(false);
  const montrerImage = statut.type === "image" && statut.image && !imageMorte;
  return (
    <div className="bulle__statut">
      {montrerImage ? (
        <img
          className="bulle__statut-vignette"
          src={statut.image}
          alt=""
          onError={() => setImageMorte(true)}
        />
      ) : (
        <span
          className="bulle__statut-vignette bulle__statut-vignette--vide"
          style={statut.fond ? { background: statut.fond } : undefined}
          aria-hidden="true"
        />
      )}
      <span className="bulle__statut-texte">
        {/* Les deux côtés lisent la même bulle : l'un a répondu, l'autre a
            été répondu. Une seule phrase pour les deux dirait faux à l'un. */}
        <span className="bulle__statut-quoi">
          {moi ? "Vous avez répondu à ce statut" : "A répondu à votre statut"}
        </span>
        <span className="bulle__statut-apercu">{statut.apercu}</span>
      </span>
    </div>
  );
}

/**
 * Une bulle, et tout ce qu'on peut en faire.
 *
 * L'appui long ouvre le menu — c'est le geste de toutes les messageries. Le
 * bouton « … » est là pour la souris, qui n'appuie pas longtemps : sans lui,
 * la fonction n'existerait que sur téléphone.
 */
function Bulle({ m, moi, ctx, convo, convoId, autreId, onRepondre }) {
  const [menu, setMenu] = useState(null); // null | "options" | "palette"
  const [brouillon, setBrouillon] = useState(null); // texte en cours de correction
  // Le menu s'ouvre vers le haut, sauf quand il n'y a pas la place — voir plus bas.
  const [versLeBas, setVersLeBas] = useState(false);
  const appui = useAppuiLong(() => setMenu("options"));
  const boite = useRef(null);
  const resume = resumer(m.reactions, ctx.moi.uid);

  /* ⚠️ Le fil des messages DÉCOUPE ce qui dépasse (`overflow-y: auto`). Un menu
     posé au-dessus d'une bulle proche du haut sort donc du cadre et se retrouve
     amputé — on ne voyait plus que sa dernière ligne. On mesure au moment de
     l'ouvrir : s'il n'y a pas la place au-dessus, il s'ouvre en dessous.
     C'est la seule façon de le savoir ; le CSS seul ne mesure rien. */
  useEffect(() => {
    if (!menu) return;
    const cadre = boite.current?.getBoundingClientRect();
    const fil = boite.current?.closest(".fil-messages")?.getBoundingClientRect();
    if (!cadre || !fil) return;
    setVersLeBas(cadre.top - fil.top < 210);
  }, [menu]);

  // Le menu se referme au clic hors de lui et à Échap. En phase de CAPTURE :
  // posé en phase normale, il partirait après le clic qui vient de l'ouvrir.
  useEffect(() => {
    if (menu !== "options") return;
    const dehors = (e) => {
      if (!boite.current?.contains(e.target)) setMenu(null);
    };
    const echap = (e) => e.key === "Escape" && setMenu(null);
    document.addEventListener("pointerdown", dehors, true);
    document.addEventListener("keydown", echap);
    return () => {
      document.removeEventListener("pointerdown", dehors, true);
      document.removeEventListener("keydown", echap);
    };
  }, [menu]);

  const reagir = async (cle) => {
    setMenu(null);
    try {
      await Store.messages.reagir(convoId, m.id, cle);
    } catch (e) {
      toast(e.message || "Réaction impossible", "erreur");
    }
  };

  const enregistrer = async () => {
    const t = (brouillon || "").trim();
    // Un message corrigé jusqu'à être vide n'est pas une correction, c'est un
    // retrait : on ne le fait pas dans son dos, on referme simplement.
    if (!t || t === m.texte) return setBrouillon(null);
    setBrouillon(null);
    try {
      await Store.messages.modifier(convoId, m.id, t);
    } catch (e) {
      toast(e.message || "Correction impossible", "erreur");
    }
  };

  const supprimer = async () => {
    setMenu(null);
    const oui = await confirmer(
      "Retirer ce message ?",
      "Il disparaîtra pour vous et pour votre correspondant, et laissera « Message supprimé » à sa place.",
      "Retirer"
    );
    if (!oui) return;
    try {
      await Store.messages.supprimer(convoId, m.id);
    } catch (e) {
      toast(e.message || "Suppression impossible", "erreur");
    }
  };

  const peutModifier = moi && !m.supprime && Boolean(m.texte);

  return (
    <div
      className={`bulle${moi ? " bulle--moi" : ""}${m.audio ? " bulle--vocal" : ""}${m.supprime ? " bulle--retiree" : ""}`}
      data-mid={m.id}
      ref={boite}
      {...(m.supprime ? {} : appui.handlers)}
    >
      {/* Dans un groupe, une bulle sans nom ne dit pas qui parle —
          et à quatre, on ne devine pas. */}
      {!moi && convo?.groupe && <AuteurBulle uid={m.auteurId} ctx={ctx} />}

      {/* Un message retiré ne garde pas sa citation : elle dirait encore à quoi
          il répondait, et un retrait qui laisse la moitié de son contexte n'est
          qu'un demi-retrait. */}
      {m.repondA?.id && !m.supprime && <Citation repondA={m.repondA} ctx={ctx} />}
      {m.repondAStatut?.id && !m.supprime && (
        <CitationStatut statut={m.repondAStatut} moi={moi} />
      )}

      {m.supprime ? (
        <div className="bulle__texte bulle__retire">Message supprimé</div>
      ) : (
        <>
          {m.audio ? (
            <div className="bulle__vocal">
              <audio src={m.audio} controls preload="metadata" />
              {m.duree > 0 && <span className="petit">{dureeLisible(m.duree)}</span>}
            </div>
          ) : null}
          {m.image ? (
            <button
              type="button"
              className="bulle__image"
              onClick={() => apercuImage(m.image)}
              aria-label="Agrandir la photo"
            >
              <img src={m.image} alt="" loading="lazy" />
            </button>
          ) : null}
          {/* Un document ne s'ouvre pas dans la conversation : on ne montre
              ni ne rend un PDF ici, on donne un lien. `rel` coupe le lien
              entre l'onglet ouvert et Koinonia — sans quoi la page
              ouverte pourrait rediriger la nôtre. */}
          {m.fichier?.url ? (
            <a
              className="bulle__fichier"
              href={m.fichier.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="bulle__fichier-icone" aria-hidden="true">📄</span>
              <span className="bulle__fichier-nom">
                <strong>{m.fichier.nom || "Document"}</strong>
                <span className="petit">{poidsLisible(m.fichier.taille)} · PDF</span>
              </span>
            </a>
          ) : null}
          {brouillon !== null ? (
            <div className="bulle__edition">
              <textarea
                value={brouillon}
                autoFocus
                maxLength={2000}
                onChange={(e) => setBrouillon(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    enregistrer();
                  }
                  if (e.key === "Escape") setBrouillon(null);
                }}
              />
              <div className="bulle__edition-actions">
                <button type="button" className="btn btn--fantome btn--petit" onClick={() => setBrouillon(null)}>
                  Annuler
                </button>
                <button type="button" className="btn btn--primaire btn--petit" onClick={enregistrer}>
                  Enregistrer
                </button>
              </div>
            </div>
          ) : (
            m.texte && (
              <div className="bulle__texte">
                <TexteRiche texte={m.texte} />
              </div>
            )
          )}
        </>
      )}

      <span className="bulle__heure">
        {heure(m.createdAt)}
        {/* « modifié » se dit, il ne se cache pas : recevoir un message dont le
            texte a changé sans le savoir serait pire que de le voir corrigé. */}
        {m.modifieLe && !m.supprime && <span className="bulle__modifie"> · modifié</span>}
        {/* seulement sur mes bulles : savoir si moi j'ai lu ce qu'on
            m'écrit ne m'apprend rien */}
        {moi && !m.supprime && <Accuse message={m} convo={convo} autreId={autreId} ctx={ctx} />}
      </span>

      {/* Les réactions posées, accrochées au bas de la bulle. */}
      {resume.total > 0 && (
        <Resume
          total={resume.total}
          tetes={resume.tetes}
          className="reaction-resume--bulle"
          onClick={() => setMenu(menu === "palette" ? null : "palette")}
        />
      )}

      {!m.supprime && (
        <button
          type="button"
          className="bulle__options"
          aria-label="Options du message"
          onClick={() => setMenu(menu === "options" ? null : "options")}
        >
          <Icone nom="options" taille={16} />
        </button>
      )}

      {menu === "palette" && (
        <Palette
          mienne={resume.mienne}
          onChoisir={reagir}
          onFermer={() => setMenu(null)}
          className={`reaction-palette--bulle${versLeBas ? " est-en-bas" : ""}`}
        />
      )}

      {menu === "options" && (
        <div className={`bulle__menu${versLeBas ? " est-en-bas" : ""}`} role="menu">
          <button type="button" onClick={() => setMenu("palette")}>
            <span className="bulle__menu-pastilles">
              <Pastille cle="amen" taille={18} titre="" />
              <Pastille cle="jaime" taille={18} titre="" />
              <Pastille cle="jadore" taille={18} titre="" />
            </span>
            Réagir
          </button>
          <button
            type="button"
            onClick={() => {
              setMenu(null);
              onRepondre({ id: m.id, auteurId: m.auteurId, apercu: apercuDe(m).slice(0, 140) });
            }}
          >
            <Icone nom="repondre" taille={17} /> Répondre
          </button>
          {peutModifier && (
            <button
              type="button"
              onClick={() => {
                setMenu(null);
                setBrouillon(m.texte);
              }}
            >
              <Icone nom="crayon" taille={17} /> Modifier
            </button>
          )}
          {moi && (
            <button type="button" className="est-danger" onClick={supprimer}>
              <Icone nom="corbeille" taille={17} /> Supprimer
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FilMessages({ ctx, convoId, convo, autre, autreId, colle, onRepondre }) {
  const messages = useAbonnement((cb) => Store.messages.subscribe(convoId, cb), null, [convoId]);
  const filRef = useRef(null);

  /* ⚠️ LE SON D'OUVERTURE SUIT LA CONVERSATION, PAS LE MONTAGE. Ce composant
     se remonte pour d'autres raisons que l'ouverture d'un fil ; c'est le
     CHANGEMENT de `convoId` qui signifie « on entre quelque part ». */
  useEffect(() => {
    if (convoId) jouer("ouvert");
  }, [convoId]);

  const autres = (convo?.membres || []).filter((m) => m !== ctx.moi.uid);
  const frappe = autres.some((id) => ecritEncore(convo, id));
  useHorlogeFrappe(frappe);

  const surDefilement = () => {
    const el = filRef.current;
    if (el) colle.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // La bulle de frappe fait grandir le fil comme le fera le vrai message :
  // on recolle donc aussi à son apparition, sinon elle s'ouvrirait hors de vue.
  useLayoutEffect(() => {
    const el = filRef.current;
    if (!el || messages === null) return;
    if (colle.current) el.scrollTop = el.scrollHeight;
  }, [messages, frappe]);

  // Ouvrir la conversation vaut lecture — mais seulement quand il y a du neuf,
  // car chaque marquage est une écriture facturée.
  useEffect(() => {
    if (messages === null || !convo) return;
    const dejaVu = (convo.luA?.[ctx.moi.uid] || 0) >= (convo.dernierAt || 0);
    if (convo.lu?.[ctx.moi.uid] && dejaVu) return;
    Store.convos.markRead(convoId).catch(() => {});
  }, [messages, convo, convoId, ctx.moi.uid]);

  return (
    <div className="fil-messages" ref={filRef} onScroll={surDefilement}>
      {!messages || !messages.length ? (
        <div className="discussion__vide">Dites bonjour à {autre.nom} 👋</div>
      ) : (
        // Fragment et non <div> : `.fil-messages` aligne ses bulles à gauche ou
        // à droite avec `align-self`, ce qui ne fonctionne que si les bulles
        // sont ses enfants DIRECTS. Un conteneur intermédiaire les recentrerait
        // toutes — mes messages cesseraient d'être à droite.
        messages.map((m, i) => {
          const moi = m.auteurId === ctx.moi.uid;
          return (
            <Fragment key={m.id || i}>
              {(i === 0 || !memeJour(m.createdAt, messages[i - 1].createdAt)) && (
                <div className="separateur-jour">{jourLisible(m.createdAt)}</div>
              )}
              <Bulle
                m={m}
                moi={moi}
                ctx={ctx}
                convo={convo}
                convoId={convoId}
                autreId={autreId}
                onRepondre={onRepondre}
              />
              {/* « Vu par … » — 24 octobre 2026, demandé par Josaphat : dans
                  un GROUPE, sous mon DERNIER message, les prénoms de ceux qui
                  l'ont ouvert. Les deux traits disent « tous » ; cette ligne
                  dit QUI, pendant que ce n'est pas encore tout le monde. */}
              {Boolean(convo?.groupe) && moi &&
                m.id === [...messages].reverse().find((x) => x.auteurId === ctx.moi.uid)?.id && (
                <VuPar convo={convo} message={m} ctx={ctx} />
              )}
            </Fragment>
          );
        })
      )}

      {frappe && (
        <div className="bulle bulle--frappe" aria-live="polite" aria-label={`${autre.nom} est en train d'écrire`}>
          <span className="frappe-points">
            <i />
            <i />
            <i />
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Annonce « en train d'écrire », sans ruiner personne.
 *
 * Chaque annonce est une écriture dans la base, et les écritures sont facturées.
 * Écrire à chaque touche coûterait une écriture par lettre — quelques centaines
 * pour un message un peu long, multipliées par tous les membres. On n'annonce
 * donc qu'une fois toutes les 3,5 secondes tant que la frappe continue, et l'on
 * éteint dès qu'elle s'arrête ou que l'écran se ferme : celui qui lit voit la
 * même chose, pour cent fois moins cher.
 */
function useAnnonceFrappe(convoId) {
  const dernier = useRef(0);
  const extinction = useRef(null);
  const allume = useRef(false);

  const eteindre = () => {
    clearTimeout(extinction.current);
    if (!allume.current) return;
    allume.current = false;
    dernier.current = 0;
    Store.convos.frappe?.(convoId, false).catch(() => {});
  };

  // Fermer l'écran en pleine phrase ne doit pas laisser les points battre
  // indéfiniment chez l'autre.
  useEffect(() => eteindre, [convoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const frapper = () => {
    clearTimeout(extinction.current);
    // au-delà de 4 s sans frappe, on considère que la phrase est finie
    extinction.current = setTimeout(eteindre, 4000);
    if (Date.now() - dernier.current < 3500) return;
    dernier.current = Date.now();
    allume.current = true;
    Store.convos.frappe?.(convoId, true).catch(() => {});
  };

  return { frapper, eteindre };
}

/** Qui parle, au-dessus d'une bulle reçue dans un groupe. */
function AuteurBulle({ uid, ctx }) {
  const auteur = useMembre(ctx, uid);
  return (
    <span className="bulle__auteur">
      {auteur.nom}
      <Certifie membre={auteur} taille={11} />
    </span>
  );
}

/** La zone de saisie : elle grandit avec le texte, Entrée envoie. */
/** Ce à quoi on est en train de répondre, au-dessus du champ de saisie. */
function BandeauReponse({ reponse, ctx, onAnnuler }) {
  const auteur = useMembre(ctx, reponse.auteurId);
  return (
    <div className="reponse-a">
      <Icone nom="repondre" taille={16} />
      <span className="reponse-a__texte">
        <strong>Réponse à {auteur.nom}</strong>
        <span className="petit texte-doux">{reponse.apercu}</span>
      </span>
      <button type="button" className="icon-btn" aria-label="Annuler la réponse" onClick={onAnnuler}>
        <Icone nom="fermer" taille={16} />
      </button>
    </div>
  );
}

function BarreEnvoi({ ctx, convoId, autreId, avantEnvoi, reponse, onAnnulerReponse }) {
  const [texte, setTexte] = useState("");
  const [envoiVocal, setEnvoiVocal] = useState(false);
  const [envoiPiece, setEnvoiPiece] = useState(false);
  const [menuPiece, setMenuPiece] = useState(false);
  const champRef = useRef(null);
  const frappe = useAnnonceFrappe(convoId);
  const micro = useEnregistreur();

  // La hauteur suit le contenu, jusqu'à un plafond : au-delà, c'est au fil des
  // messages de garder la place, pas à la zone de saisie de manger l'écran.
  // Et la barre de défilement n'apparaît qu'à ce moment-là — avant, elle
  // n'aurait rien à faire défiler.
  useLayoutEffect(() => {
    const t = champRef.current;
    if (!t) return;
    t.style.height = "auto";
    const voulue = t.scrollHeight;
    t.style.height = Math.min(140, voulue) + "px";
    t.style.overflowY = voulue > 140 ? "auto" : "hidden";
  }, [texte]);

  useEffect(() => {
    champRef.current?.focus();
  }, [convoId]);

  const envoyer = async (e) => {
    e?.preventDefault();
    const t = texte.trim();
    if (!t) return;
    setTexte(""); // vidé tout de suite : on ne fait pas attendre celui qui écrit
    frappe.eteindre();
    // Envoyer son propre message ramène TOUJOURS en bas du fil, même si l'on
    // était remonté lire : on vient de parler, on veut voir ce qu'on a dit.
    avantEnvoi?.();
    try {
      // Plus de notification pour un message : elle encombrait la liste des
      // alertes alors que le message lui-même est déjà l'alerte. Le compte des
      // conversations non lues s'affiche sur l'icône Messages, et l'alerte sur
      // l'appareil part depuis `app.js`, en suivant les conversations.
      await Store.messages.send(convoId, t, reponse ? { repondA: reponse } : {});
      /* ⚠️ APRÈS L'ENVOI, JAMAIS AVANT. Un son joué au moment du clic
         mentirait : il dirait « parti » alors que le serveur peut refuser. On
         entend donc le départ quand le départ a eu lieu. */
      jouer("envoye");
      onAnnulerReponse?.(); // la citation a servi, elle s'efface
    } catch (err) {
      toast(err.message || "Message non envoyé", "erreur");
      setTexte(t); // il n'est pas parti : on lui rend son texte
    }
  };

  /**
   * Une photo ou un document joint au message.
   *
   * Les deux passent par le même chemin — seul le dossier de dépôt et la règle
   * qui le protège diffèrent. Le contrôle des octets réels a lieu dans
   * `Store.upload` : un exécutable renommé en .pdf n'arrive pas jusqu'ici.
   */
  const joindre = (quoi) => {
    const champ = document.createElement("input");
    champ.type = "file";
    champ.accept = quoi === "photo" ? "image/*" : "application/pdf,.pdf";
    champ.onchange = async () => {
      const f = champ.files?.[0];
      if (!f) return;
      setEnvoiPiece(true);
      avantEnvoi?.();
      try {
        const url = await Store.upload(f, quoi === "photo" ? "image" : "document");
        await Store.messages.send(
          convoId,
          "",
          quoi === "photo"
            ? { image: url }
            : { fichier: { url, nom: f.name.slice(0, 120), taille: f.size } }
        );
        jouer("envoye");
      } catch (err) {
        toast(err.message || "Pièce jointe non envoyée", "erreur");
      }
      setEnvoiPiece(false);
    };
    champ.click();
  };

  const terminerVocal = async (garder) => {
    const pris = await micro.arreter(garder);
    if (!pris) return;
    setEnvoiVocal(true);
    avantEnvoi?.();
    try {
      const url = await Store.upload(pris.fichier, "audio");
      await Store.messages.send(convoId, "", { audio: url, duree: pris.duree });
    } catch (err) {
      toast(err.message || "Message vocal non envoyé", "erreur");
    }
    setEnvoiVocal(false);
  };

  // Pendant l'enregistrement, la barre entière devient l'enregistreur : garder
  // le champ de saisie ferait croire qu'on peut faire les deux à la fois.
  if (micro.enCours)
    return (
      <div className="barre-envoi barre-envoi--vocal">
        <button className="icon-btn" aria-label="Annuler l'enregistrement" onClick={() => terminerVocal(false)}>
          <Icone nom="fermer" taille={18} />
        </button>
        <span className="vocal-encours">
          <span className="vocal-point" />
          {dureeLisible(micro.secondes)}
        </span>
        <button className="icon-btn icon-btn--envoi" aria-label="Envoyer le vocal" onClick={() => terminerVocal(true)}>
          <Icone nom="envoyer" taille={18} />
        </button>
      </div>
    );

  return (
    <form className="barre-envoi" onSubmit={envoyer}>
      {reponse && <BandeauReponse reponse={reponse} ctx={ctx} onAnnuler={onAnnulerReponse} />}
      {/* Le trombone reste TOUJOURS visible, même pendant qu'on écrit : joindre
          une photo à une phrase qu'on vient de taper est le cas le plus
          courant, pas l'exception. */}
      <button
        className="icon-btn"
        type="button"
        aria-label="Joindre une photo ou un document"
        disabled={envoiPiece}
        onClick={() => setMenuPiece((v) => !v)}
      >
        <Icone nom="trombone" taille={18} />
      </button>
      {menuPiece && (
        <div className="menu-piece" role="menu">
          <button type="button" onClick={() => { setMenuPiece(false); joindre("photo"); }}>
            🖼️ Photo
          </button>
          <button type="button" onClick={() => { setMenuPiece(false); joindre("document"); }}>
            📄 Document PDF
          </button>
        </div>
      )}
      <textarea
        ref={champRef}
        rows={1}
        maxLength={2000}
        placeholder="Écrire un message…"
        value={texte}
        onChange={(e) => {
          setTexte(e.target.value);
          if (e.target.value.trim()) frappe.frapper();
          else frappe.eteindre();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            envoyer();
          }
        }}
      />
      {/* Le micro remplace l'envoi tant que rien n'est écrit : deux boutons
          côte à côte sur un téléphone, c'est un appui sur deux qui se trompe. */}
      {texte.trim() ? (
        <button className="icon-btn icon-btn--envoi" type="submit" aria-label="Envoyer">
          <Icone nom="envoyer" taille={18} />
        </button>
      ) : (
        <button
          className="icon-btn icon-btn--envoi"
          type="button"
          aria-label="Enregistrer un message vocal"
          disabled={envoiVocal}
          onClick={micro.demarrer}
        >
          <Icone nom="micro" taille={18} />
        </button>
      )}
    </form>
  );
}

function Discussion({ ctx, convoId, convo }) {
  const [etat, setEtat] = useState({ phase: "ouverture" });
  // À quel message on répond. Il vit ICI et non dans la barre d'envoi : c'est
  // le fil qui le désigne, et la barre qui l'utilise — deux enfants différents.
  const [reponse, setReponse] = useState(null);
  const nomConversation = useNomConversation(convo, ctx);
  // Partagé entre le fil et la barre d'envoi : le fil le met à jour au
  // défilement, l'envoi le force à vrai. Un ref et non un état — le changer ne
  // doit rien redessiner, il ne sert qu'au moment de repositionner.
  const colle = useRef(true);

  useEffect(() => {
    let vivant = true;
    setEtat({ phase: "ouverture" });
    (async () => {
      const convo = await Store.convos.get(convoId);
      if (!vivant) return;
      // Une conversation dont je ne fais pas partie ne s'ouvre pas — et on le
      // dit, plutôt que de laisser un écran vide faire croire à une panne.
      if (!convo || !convo.membres.includes(ctx.moi.uid)) return setEtat({ phase: "interdit" });
      const autreId = convo.membres.find((m) => m !== ctx.moi.uid) || ctx.moi.uid;
      const autre = (await ctx.profil(autreId)) || { nom: "Membre", uid: autreId };
      if (vivant) setEtat({ phase: "prete", autreId, autre });
    })();
    return () => {
      vivant = false;
    };
  }, [convoId, ctx]);

  if (etat.phase === "ouverture") return <div className="discussion__vide">Ouverture…</div>;
  if (etat.phase === "interdit")
    return (
      <Vide
        symbole="🔒"
        titre="Conversation introuvable"
        texte="Vous n'avez pas accès à cette discussion."
      />
    );

  const nomGroupe = nomConversation;

  return (
    <>
      <header className="discussion__tete">
        <a className="icon-btn discussion__retour" href="#/messages" aria-label="Retour">
          <Icone nom="retour" taille={20} />
        </a>
        {convo?.groupe ? (
          <span className="discussion__qui">
            <span className="convo-groupe" aria-hidden="true">👥</span>
            <span>
              <strong>{nomGroupe}</strong>
              <span className="petit texte-doux">{convo.membres.length} personnes</span>
            </span>
          </span>
        ) : (
          <a className="discussion__qui" href={`#/profil/${etat.autreId}`}>
            <Avatar membre={etat.autre} taille={40} />
            <span>
              <strong>
                {etat.autre.nom}
                <Certifie membre={etat.autre} taille={14} />
              </strong>
              {/* La présence remplace l'église quand elle a quelque chose à dire :
                  savoir si l'autre est là compte davantage, au moment d'écrire,
                  que de savoir où il va le dimanche. */}
              <Presence ctx={ctx} uid={etat.autreId} />
              {!ctx.signaux?.instantane?.().vuA?.get(etat.autreId) && (
                <span className="petit texte-doux">{etat.autre.eglise || etat.autre.ville || ""}</span>
              )}
            </span>
          </a>
        )}
      </header>
      <FilMessages
        ctx={ctx}
        convoId={convoId}
        convo={convo}
        autre={etat.autre}
        autreId={etat.autreId}
        colle={colle}
        onRepondre={(r) => {
          setReponse(r);
          colle.current = true;
        }}
      />
      <BarreEnvoi
        ctx={ctx}
        convoId={convoId}
        autreId={etat.autreId}
        avantEnvoi={() => (colle.current = true)}
        reponse={reponse}
        onAnnulerReponse={() => setReponse(null)}
      />
    </>
  );
}

/* ------------------------------------------------------------------- écran */

export default function Messages({ ctx, params }) {
  const ouvert = params.id || null;

  // UN seul abonnement aux conversations pour tout l'écran. La liste en a
  // besoin, et la discussion aussi — c'est de là que viennent les accusés de
  // lecture et le « en train d'écrire ». Deux abonnements sur la même requête,
  // ce serait deux fois les lectures pour exactement la même information.
  const convos = useAbonnement((cb) => Store.convos.subscribe(cb), null, []);
  const convoOuverte = (convos || []).find((c) => c.id === ouvert) || null;

  return (
    <div className={`messagerie${ouvert ? " est-ouvert" : ""}`}>
      <aside className="convos">
        <div className="convos__tete">
          <h2>Messages</h2>
          <button className="btn btn--fantome btn--petit" onClick={() => creerConversation(ctx)}>
            ＋ Nouveau
          </button>
        </div>
        <AQuiEcrire ctx={ctx} />
        <div>
          <ListeConvos convos={convos} ctx={ctx} ouvert={ouvert} />
        </div>
      </aside>
      <section className="discussion">
        {ouvert ? (
          /* ⚠️ `key={ouvert}` — 20 octobre 2026, trouvé à l'audit. Sans clé,
             changer de conversation gardait l'état de l'ancienne : une
             citation armée dans A restait affichée dans B, et s'attachait au
             prochain message de B si on l'ignorait. La clé remonte Discussion
             à neuf, citation et brouillon compris. */
          <Discussion key={ouvert} ctx={ctx} convoId={ouvert} convo={convoOuverte} />
        ) : (
          <Vide
            symbole="💬"
            titre="Vos messages privés"
            texte="Choisissez une conversation à gauche, ou trouvez un membre dans l'annuaire."
          />
        )}
      </section>
    </div>
  );
}
